import AmplitudeTracker from 'common/services/trackers/amplitude';
import { Flavours } from 'deposit/services/types';
import {
  AmplitudeDepositFlow,
  AmplitudeDepositMethodType,
  AmplitudeLinkType,
  AmplitudePurchaseType,
  AmplitudeSourceType,
  AmplitudeVoucherType,
} from './types';

import { useGetIntegrationData } from 'common/helpers/hooks';
import { OS_SYSTEM } from 'common/helpers/utils';

class AmplitudeAnalytics {
  tracker: typeof AmplitudeTracker = AmplitudeTracker;

  //EVENTS ----

  /**
   *  Triggers when we detected cookies being blocked
   *
   * @param boolean SDK is SDK integration
   * @param string OS users operating system
   * @param string UA browsers user agent
   *
   */

  cookiesBlocked = (SDK: boolean, OS: OS_SYSTEM, UA: string) => {
    this.tracker.trackEvent('Cookies Blocked', {
      SDK: SDK,
      OS: OS,
      UA: UA,
    });
  };

  /**
   * Triggers when the user opens the app
   *
   * @param boolean isLogged Show if the user was logged in when the app was opened
   * @param AmplitudeSourceType sourceType Source type of the app is opening
   * @param string sourceName  Source name of the app is opening
   * @param token token Token to identify the operation
   * @param merchantId merchantId Merchant Id when we have it
   * @param boolean isSdkIntegrated The deposit was opened from front sdk
   * @param depositData depositData An object with the deposit data: amount, currenty, flow
   */
  platformOpened = (
    isLogged: boolean,
    sourceType: AmplitudeSourceType,
    sourceName: string,
    token: string,
    merchantId?: number,
    isSdkIntegrated?: boolean,
    depositData?: {
      'Deposit Amount': number;
      'Deposit Currency': string;
      'Deposit Flow': AmplitudeDepositFlow;
    },
  ) => {
    const UID = `${token + merchantId}`;
    const integrationData = useGetIntegrationData();
    const operationIdentifer =
      sourceType === AmplitudeSourceType.MERCHANT_SITE
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.setUserProps({ 'Product Type': 'OT' });

    this.tracker.trackUniqueEvent('Platform Opened', UID, {
      'Is Logged': isLogged,
      'Source Type': sourceType,
      'Source Name': sourceName,
      'Merchant Id': merchantId,
      'SDK Integrated': isSdkIntegrated,
      ...depositData,
      ...operationIdentifer,
      ...integrationData,
    });
  };

  /**
   * Triggers when the user click on dynamic link to continue with app
   *
   * @param AmplitudeSourceType sourceType Source type of the app is opening
   * @param string sourceName  Source name of the app is opening
   * @param LinkType  linkType Dynamic Link Type
   * @param Flavour  flavour Show in which flavour the method is selected
   */
  dynamicLinkSelected = (sourceName: string, linkType: AmplitudeLinkType, flavour?: Flavours) => {
    this.tracker.trackEvent('Dynamic Link Selected', {
      'Source Type': 'Web',
      'Source Name': sourceName,
      Flavour: flavour,
      'Dynamic Link Type': linkType,
    });
  };

  /**
   * Phone number entered and the user selects on "Continue"
   *
   * @param string country Country code of the phone number
   * @param token string External id of the flow
   */
  phoneNumberEntered = (country: string, token: string, sourceType?: AmplitudeSourceType) => {
    const operationIdentifer =
      sourceType === AmplitudeSourceType.MERCHANT_SITE
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.trackEvent('Phone Number Entered', {
      'Country Entered': country,
      'Source Type': sourceType,
      ...operationIdentifer,
    });
  };

  /**
   * When the user entered the code recieved
   * @param sourceType AmplitudeSourceType
   * @param token string External id of the flow
   */
  codeEntered = (sourceType: AmplitudeSourceType, token: string) => {
    const operationIdentifer =
      sourceType === AmplitudeSourceType.MERCHANT_SITE
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.trackEvent('Code Entered', {
      'Source Type': sourceType,
      ...operationIdentifer,
    });
  };

  /**
   * When the user submits a promo code
   * @param promoCode promoCode string
   * @param token depositExternalId string
   * @param sourceType sourceType AmplitudeSourceType
   */

  promoCodeSent = (promoCode: string, token: string, sourceType: AmplitudeSourceType) => {
    this.tracker.trackEvent('Promo Code Sent', {
      'Promo Code': promoCode,
      'Deposit External Id': token,
      'Source Type': sourceType,
    });
  };

  /**
   * When the user selected the "Pay" buttonin the checkout screen
   *
   * @param string paymentMethodName  - Payment method of the transaction
   * @param AmplitudePurchaseType purchaseType - This is show where the purchase is made
   * @param Flavours flavour - Show in which flavour the user is making the purchase
   * @param token string External id of the flow
   */
  purchaseStarted = (
    paymentMethodName: string,
    purchaseType: AmplitudePurchaseType,
    flavour: Flavours = Flavours.BASIC,
    token: string,
  ) => {
    const operationIdentifer =
      purchaseType === AmplitudePurchaseType.MERCHANT_DEPOSIT
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.trackEvent('Merchant Deposit Purchase Started', {
      'Purchase Type': purchaseType,
      'Payment Method Name': paymentMethodName,
      Flavour: flavour,
      ...operationIdentifer,
    });
  };

  /**
   * When enters a voucher with Card Data
   * @param token string Deposit external id
   */

  voucherCardDataEntered = (token: string) => {
    this.tracker.trackEvent('Voucher Card Number Data Entered', {
      'Deposit External Id': token,
    });
  };

  /**
   * When enters a voucher with Card Data
   * @param token string Deposit external id
   */

  voucherCodeEntered = (token: string) => {
    this.tracker.trackEvent('Voucher Code Entered', {
      'Deposit External Id': token,
    });
  };

  /**
   * When the user selected the "Pay" button in the checkout screen for voucher
   *
   * @param number amount - Amount of the transaction
   * @param string currency - Currency of the transaction
   * @param string voucherType  - Selected or entered
   * @param flow flow - Show in which flavour the user is making the purchase
   * @param token string External id of the flow
   */
  voucherDepositStarted = (
    voucherType: AmplitudeVoucherType,
    amount: number,
    currency: string,
    token: string,
    flow?: string,
  ) => {
    this.tracker.trackEvent('Merchant Deposit Voucher Started', {
      'Voucher Type': voucherType,
      'Voucher Amount': amount,
      'Voucher Currency': currency,
      'OT Flow': flow,
      'Deposit External Id': token,
    });
  };

  /**
   * When users reach completed page
   * @param token string Deposit external id
   */

  depositCompletedPage = (token: string) => {
    this.tracker.trackEvent('Deposit Completed Page', {
      'Deposit External Id': token,
    });
  };

  /**
   *
   * When the user gets asked required fields error
   *
   * @param token token - string
   * @param merchant merchant - Merchant id and name
   * @param sourceType sourceType - AmplitudeSourceType
   * @param kycRequested kycRequested - boolean
   * @param sofRequested sofRequested - boolean
   * @param fieldsRequested fieldsRequest - string array of fields
   */

  requiredFieldsRequested = (
    token: string,
    sourceType: AmplitudeSourceType,
    merchant?: { id?: number; name?: string },
    kycRequested?: boolean,
    sofRequested?: boolean,
    fieldsRequested?: string[],
  ) => {
    const operationIdentifer =
      sourceType === AmplitudeSourceType.MERCHANT_SITE
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.trackEvent('Required Fields Validation', {
      ...operationIdentifer,
      'Merchant Id': merchant?.id,
      'Merchant Name': merchant?.name,
      'KYC Request': kycRequested,
      'SOF Request': sofRequested,
      'Fields Requested': fieldsRequested?.join(', '),
    });
  };

  paymentLinkPaidStarted = (source: string) => {
    this.tracker.trackEvent('Payment Link Paid Started', { Source: source });
  };

  paymentLinkPaymentFinalized = (status: 'Completed' | 'Rejected') => {
    this.tracker.trackEvent('Payment Link Payment Finalized', { Status: status });
  };

  paymentLinkMehodSelected = (method: AmplitudeDepositMethodType) => {
    this.tracker.trackEvent('Payment Link Method Selected', { 'Payment Method Type': method });
  };

  /**
   *
   * When the user addas a new creditCard
   *
   * @param sourceType sourceType - AmplitudeSourceType
   * @param token token - string
   *
   */

  creditCardAdded = (token: string, sourceType: AmplitudeSourceType) => {
    const operationIdentifer =
      sourceType === AmplitudeSourceType.MERCHANT_SITE
        ? { 'Deposit External Id': token }
        : { 'Reference External Id': token };

    this.tracker.trackEvent('Credit Card Added', {
      ...operationIdentifer,
      'Source Type': sourceType,
    });
  };

  creditCardSelected = () => {
    this.tracker.trackEvent('Credit/Debit Card Selected');
  };

  /**
   * When the user selected "Yes, send Code" in wallet transfer flow
   */
  walletTransferOnSendCode = () => {
    this.tracker.trackEvent('Code Requested');
  };

  /**
   * When the user selected "This is not my number"
   */
  walletTransferOnWrongNumber = () => {
    this.tracker.trackEvent('Wrong Number Notified');
  };

  /**
   * When the user completes the deposit and gets redirected to the merchant
   */
  merchantRedirection = () => {
    this.tracker.trackEvent('OT Deposit Redirect');
  };

  /**
   * When the user starts a payment process and the limit time is reached
   */
  waitingPoolingTimeout = () => {
    this.tracker.trackEvent('OT Waiting time ended');
  };

  /**
   * When the user restarts the payment check process
   */
  waitingPoolingRestart = () => {
    this.tracker.trackEvent('OT Restart waiting time');
  };
}

export default new AmplitudeAnalytics();
